'use client';

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import AutoScroll from 'embla-carousel-auto-scroll';
import styles from './EndlessAutoScrollCarousel.module.css';

export interface EndlessAutoScrollCarouselProps {
  children: React.ReactNode;
  slideWidth?: string;
  gap?: string;
  speed?: number;
  className?: string;
  ariaLabel?: string;
  as?: 'section' | 'div';
  /** When false, auto-scroll is stopped (e.g. prefers-reduced-motion). */
  play?: boolean;
  stopOnMouseEnter?: boolean;
  stopOnFocusIn?: boolean;
  stopOnInteraction?: boolean;
}

/**
 * Reusable endless auto-scrolling carousel using Embla with loop and
 * embla-carousel-auto-scroll. Accepts custom slide content.
 */
export const EndlessAutoScrollCarousel: React.FC<
  EndlessAutoScrollCarouselProps
> = ({
  children,
  slideWidth = '308px',
  gap = '1px',
  speed = 1,
  className,
  ariaLabel,
  as = 'div',
  play = true,
  stopOnMouseEnter = true,
  stopOnFocusIn = true,
  stopOnInteraction,
}) => {
  const autoScrollRef = useRef<ReturnType<typeof AutoScroll> | null>(null);

  const plugins = useMemo(() => {
    const autoScroll = AutoScroll({
      playOnInit: play,
      stopOnFocusIn,
      stopOnMouseEnter,
      ...(stopOnInteraction !== undefined ? { stopOnInteraction } : {}),
      speed,
    });
    autoScrollRef.current = autoScroll;
    return [autoScroll];
  }, [play, speed, stopOnFocusIn, stopOnInteraction, stopOnMouseEnter]);

  const emblaOptions = useMemo(
    () => ({
      loop: true,
      slidesToScroll: 1,
      dragFree: true,
    }),
    [],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, plugins);

  const restartAutoScroll = useCallback(() => {
    if (!emblaApi) return;

    emblaApi.reInit(emblaOptions);

    const pluginsObj = emblaApi.plugins();
    const autoScrollPlugin = (pluginsObj as { autoScroll?: unknown })?.autoScroll;
    if (autoScrollPlugin) {
      autoScrollRef.current = autoScrollPlugin as ReturnType<typeof AutoScroll>;
    }

    const autoScroll = emblaApi.plugins()?.autoScroll as
      | { play?: () => void; stop?: () => void }
      | undefined;

    if (!autoScroll) return;

    if (play) {
      autoScroll.play?.();
    } else {
      autoScroll.stop?.();
    }
  }, [emblaApi, emblaOptions, play]);

  useEffect(() => {
    if (!emblaApi) return;
    restartAutoScroll();
  }, [emblaApi, gap, slideWidth, restartAutoScroll]);

  useEffect(() => {
    if (!emblaApi) return;

    const root = emblaApi.rootNode();
    if (!root || typeof ResizeObserver === 'undefined') return;

    let layoutRafId = 0;
    const scheduleRestart = () => {
      if (layoutRafId) window.cancelAnimationFrame(layoutRafId);
      layoutRafId = window.requestAnimationFrame(() => {
        layoutRafId = 0;
        restartAutoScroll();
      });
    };

    const resizeObserver = new ResizeObserver(scheduleRestart);
    resizeObserver.observe(root);

    const images = Array.from(root.querySelectorAll('img'));
    images.forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', scheduleRestart, { once: true });
      image.addEventListener('error', scheduleRestart, { once: true });
    });

    return () => {
      resizeObserver.disconnect();
      if (layoutRafId) window.cancelAnimationFrame(layoutRafId);
      images.forEach((image) => {
        image.removeEventListener('load', scheduleRestart);
        image.removeEventListener('error', scheduleRestart);
      });
    };
  }, [emblaApi, restartAutoScroll]);

  useEffect(() => {
    if (!emblaApi) return;

    const autoScroll = emblaApi.plugins()?.autoScroll as
      | { play?: () => void; stop?: () => void }
      | undefined;

    if (!autoScroll) return;

    if (play) {
      autoScroll.play?.();
    } else {
      autoScroll.stop?.();
    }
  }, [emblaApi, play]);

  const handleMouseLeave = useCallback(() => {
    const plugin = autoScrollRef.current;
    if (!plugin) return;
    const p = plugin as { play?: () => void; resume?: () => void; start?: () => void };
    if (typeof p.play === 'function') {
      try {
        p.play();
      } catch (err) {
        console.warn('Failed to resume auto-scroll:', err);
      }
    } else if (typeof p.resume === 'function') {
      try {
        p.resume();
      } catch (err) {
        console.warn('Failed to resume auto-scroll:', err);
      }
    } else if (typeof p.start === 'function') {
      try {
        p.start();
      } catch (err) {
        console.warn('Failed to resume auto-scroll:', err);
      }
    }
  }, []);

  const slides = useMemo(
    () => React.Children.toArray(children).filter(Boolean),
    [children],
  );

  if (slides.length === 0) {
    return null;
  }

  const Wrapper = as === 'section' ? 'section' : 'div';

  return (
    <Wrapper
      className={className ? `${styles.wrapper} ${className}` : styles.wrapper}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      onMouseLeave={stopOnMouseEnter ? handleMouseLeave : undefined}
    >
      <div className={styles.viewport} ref={emblaRef}>
        <div className={styles.container}>
          {slides.map((slide, index) => (
            <div
              key={index}
              className={styles.slide}
              style={{
                ['--slide-width' as string]: slideWidth,
                ['--gap' as string]: gap,
              }}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
    </Wrapper>
  );
};
